import { memo, useCallback, useState } from 'react';
import { Bell, ChevronRight } from 'lucide-react';
import { cn } from '@clarix/ui';
import { timeAgo } from '@clarix/utils';
import { NotificationFeedRow } from './NotificationFeedRow';
import type { FeedCategoryMap, FeedCluster, FeedNotification } from './types';

interface NotificationFeedGroupProps {
  cluster: FeedCluster;
  categories: FeedCategoryMap;
  onMarkRead?: (id: string) => void;
  onView?: (notification: FeedNotification) => void;
  newIds?: Set<string>;
}

/**
 * A collapsed cluster of repeat notifications about one record — the row the
 * feed shows in place of twelve near-identical ones. Expanding reveals the
 * members as compact {@link NotificationFeedRow}s, so the click target and
 * read/navigate behaviour stay exactly the same as an unclustered row.
 */
export const NotificationFeedGroup = memo(function NotificationFeedGroup({
  cluster,
  categories,
  onMarkRead,
  onView,
  newIds,
}: NotificationFeedGroupProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { representative: rep, children, count } = cluster;

  const config = categories[rep.category];
  const Icon = rep.appearance?.icon ?? config?.icon ?? Bell;
  const tileBg = rep.appearance?.bg ?? config?.bg ?? 'bg-muted';
  const tileColor = rep.appearance?.color ?? config?.color ?? 'text-muted-foreground';

  const unreadChildCount = children.filter((n) => !n.isRead).length;
  const hasUnread = unreadChildCount > 0;

  const handleToggle = useCallback(() => setIsExpanded((prev) => !prev), []);

  const label = config?.label ?? rep.category;
  const title = rep.tag ? `${label} on ${rep.tag}` : `${label} updates`;

  return (
    <div className="border-border border-b last:border-b-0">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isExpanded}
        className={cn(
          'relative flex w-full cursor-pointer items-center gap-2.5 px-4 py-2 transition-colors duration-75',
          'hover:bg-accent text-left',
          hasUnread && 'bg-primary-light/20',
        )}
      >
        {hasUnread && (
          <span className="bg-primary absolute left-[7px] top-1/2 h-1 w-1 -translate-y-1/2 rounded-full" />
        )}

        {/* Category tile + stack badge */}
        <div className="relative flex-shrink-0">
          <div
            className={cn('flex h-[30px] w-[30px] items-center justify-center rounded-lg', tileBg)}
          >
            <Icon size={14} strokeWidth={1.5} className={tileColor} />
          </div>
          <span
            className={cn(
              'absolute -bottom-0.5 -right-0.5 h-[15px] w-[15px] rounded-full',
              'border-border bg-surface flex items-center justify-center border-[1.5px]',
              'text-muted-foreground text-xxs font-bold',
            )}
          >
            {count}
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-[5px]">
            <span className="text-foreground truncate text-lg font-medium">{title}</span>
            <span className="bg-muted text-muted-foreground flex-shrink-0 rounded-[5px] px-[5px] py-px text-xxs font-semibold">
              {hasUnread ? `${unreadChildCount} new` : String(count)}
            </span>
          </div>
          <p className="text-muted-foreground mt-px truncate text-sm">{rep.body ?? rep.title}</p>
        </div>

        <span className="text-muted-foreground mt-0.5 flex-shrink-0 self-start text-xs">
          {timeAgo(rep.createdAt)}
        </span>

        <div
          className={cn(
            'flex h-[18px] w-[18px] flex-shrink-0 items-center justify-center',
            'text-muted-foreground self-center transition-transform duration-200',
            isExpanded && 'rotate-90',
          )}
        >
          <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
        </div>
      </button>

      <div
        className={cn(
          'overflow-hidden transition-[max-height] duration-300 ease-in-out',
          isExpanded ? 'max-h-[500px]' : 'max-h-0',
        )}
      >
        {children.map((n) => (
          <NotificationFeedRow
            key={n.id}
            notification={n}
            categories={categories}
            onMarkRead={onMarkRead}
            onView={onView}
            isNew={newIds?.has(n.id)}
            compact
          />
        ))}
      </div>
    </div>
  );
});
